/** @jsxImportSource @emotion/react */
import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { css } from '@emotion/react';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import type { CSSProperties } from 'react';

import { pxToRem } from '@/Client/Styles/pxToRem';
import type {
  PhotoDetailKeyValueModel,
  PhotoDetailMaterialModel,
  PhotoDetailModel,
} from './photoDetailModels';

const RICH_SECTION_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 0;
  border-bottom: 1px solid var(--pd-rule-soft, ${COLORS.strokeFaded});
`;

const PLAIN_SECTION_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px 0 28px;
  border-bottom: 1px solid var(--pd-rule-soft, ${COLORS.strokeFaded});
  background: var(--pd-pane-bg, ${COLORS.white});
`;

const SECTION_TITLE_STYLE = css`
  color: var(--pd-muted, ${COLORS.midtoneFaded});
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const DETAIL_GRID_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: ${pxToRem(12.5)};
`;

const DETAIL_ROW_STYLE = css`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: baseline;
  gap: 9px 16px;
  min-width: 0;
`;

const DETAIL_LABEL_STYLE = css`
  color: var(--pd-muted, ${COLORS.midtoneFaded});
  font-size: ${pxToRem(12.5)};
  overflow-wrap: anywhere;
`;

const DETAIL_VALUE_STYLE = css`
  color: var(--pd-pane-text, ${COLORS.ink});
  font-size: ${pxToRem(12.5)};
  font-weight: 500;
  text-align: right;
  white-space: nowrap;
`;

/** The value with a host's control beside it, keeping both in the row's value column. */
const DETAIL_VALUE_WITH_CONTROL_STYLE = css`
  label: PHOTO_DETAIL_VALUE_WITH_CONTROL;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  justify-self: end;
`;

/* Mockup `.po-grid-ai`: Kai named as the actor, as a chip at the value column's edge rather than a
   badge repeated on every thumbnail in the rail. */
const DETAIL_VALUE_ACCENT_STYLE = css`
  label: PHOTO_DETAIL_VALUE_ACCENT;
  justify-self: end;
  padding: 1px 6px;
  background: ${COLORS.accent};
  color: ${COLORS.ink};
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

/** The labour basis, beside its label rather than in the value column. */
const DETAIL_LABEL_HINT_STYLE = css`
  label: PHOTO_DETAIL_LABEL_HINT;
  color: var(--pd-muted, ${COLORS.midtoneFaded});
  font-size: ${pxToRem(11.5)};
`;

const DETAIL_VALUE_SUCCESS_STYLE = css`
  color: #60926c;
  font-weight: 800;
`;

const MATERIALS_TABLE_STYLE = css`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--pd-rule-soft, ${COLORS.strokeFaded});
  border-radius: 0;
  overflow: hidden;
`;

const MATERIAL_ROW_STYLE = css`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto auto;
  gap: 10px;
  padding: 9px 12px;
  border-bottom: 1px solid var(--pd-rule-soft, ${COLORS.strokeFaded});
  font-size: ${pxToRem(13)};

  &:last-of-type {
    border-bottom: 0;
  }
`;

const MATERIAL_VENDOR_STYLE = css`
  display: block;
  margin-top: 2px;
  color: var(--pd-muted, ${COLORS.midtoneFaded});
  font-size: ${pxToRem(11)};
`;

const MATERIAL_EMPTY_STYLE = css`
  color: #9b9691;
  font-size: ${pxToRem(23)};
  line-height: 1.304;
`;

const ACTIVITY_TIMELINE_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 2px 0 8px 8px;
`;

const ACTIVITY_ITEM_STYLE = css`
  position: relative;
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 16px;
  color: var(--pd-pane-text, ${COLORS.ink});
  font-size: ${pxToRem(15)};
  line-height: 1.35;

  &:not(:last-of-type)::after {
    content: '';
    position: absolute;
    top: 24px;
    bottom: -26px;
    left: 9px;
    width: 3px;
    border-radius: 0;
    background: var(--pd-rule, ${COLORS.stroke});
  }
`;

const ACTIVITY_DOT_STYLE = css`
  z-index: 1;
  width: 20px;
  height: 20px;
  margin-top: 2px;
  border: 4px solid var(--closeout-activity-dot-color, var(--pd-rule, ${COLORS.stroke}));
  border-radius: 0;
  background: var(--closeout-activity-dot-fill, #fff);
  box-sizing: border-box;
`;

const ACTIVITY_ITEM_BODY_STYLE = css`
  min-width: 0;
  white-space: pre-wrap;
`;

const ACTIVITY_TEXT_STYLE = css`
  color: var(--pd-pane-text, ${COLORS.ink});
  font-size: ${pxToRem(15)};
  line-height: 1.333;

  strong {
    color: var(--pd-pane-text, ${COLORS.ink});
    font-weight: 800;
  }
`;

const ACTIVITY_DATE_STYLE = css`
  margin-top: 7px;
  color: #96918c;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(13)};
  line-height: 1.385;
  letter-spacing: 0.01em;
`;

type ActivityNotesItem = {
  id: string;
  title?: string;
  body: string;
  when?: string;
};

type ActivityTone = 'success' | 'danger' | 'info' | 'neutral';

const activityToneColors: Record<ActivityTone, { color: string; fill?: string }> = {
  success: { color: '#62976e', fill: '#62976e' },
  danger: { color: '#b44d50' },
  info: { color: '#5c82aa' },
  neutral: { color: '${COLORS.stroke}' },
};

const getDetailSectionTestId = (title: string) => `closeout-photo-detail-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;

const getNoteParagraphs = (notes?: string): readonly string[] => notes?.split(/\n\s*\n/).map(note => note.trim()).filter(Boolean) ?? [];

const getActivityTone = (item: ActivityNotesItem): ActivityTone => {
  const text = `${item.title ?? ''} ${item.body}`.toLowerCase();
  if (/reject|delete|rework|request edit|submitted/.test(text)) return 'danger';
  if (/complete|approved|final/.test(text)) return 'success';
  if (/progress|upload|photo|started|updated|in progress/.test(text)) return 'info';
  return 'neutral';
};

const getActivityDotStyle = (tone: ActivityTone): CSSProperties & Record<'--closeout-activity-dot-color' | '--closeout-activity-dot-fill', string> => {
  const colors = activityToneColors[tone];
  return {
    '--closeout-activity-dot-color': colors.color,
    '--closeout-activity-dot-fill': colors.fill ?? '#fff',
  };
};

const getValueStyle = (tone: PhotoDetailKeyValueModel['tone']) =>
  tone === 'success' ? DETAIL_VALUE_SUCCESS_STYLE : tone === 'accent' ? DETAIL_VALUE_ACCENT_STYLE : DETAIL_VALUE_STYLE;

export const DetailSection = ({
  title,
  rows,
}: {
  title: string;
  rows?: readonly PhotoDetailKeyValueModel[];
}) => (
  <section
    css={RICH_SECTION_STYLE}
    data-testid={getDetailSectionTestId(title)}
    data-closeout-section-variant='rich-grid'
  >
    <h3 css={SECTION_TITLE_STYLE}>{title}</h3>
    <div css={DETAIL_GRID_STYLE}>
      {(rows ?? []).map(row => (
        <div key={row.label} css={DETAIL_ROW_STYLE} data-testid='closeout-photo-detail-rich-row'>
          <span css={DETAIL_LABEL_STYLE}>
            {row.label}
            {row.hint ? <span css={DETAIL_LABEL_HINT_STYLE}>{` · ${row.hint}`}</span> : null}
          </span>
          {row.control
            ? (
              <span css={DETAIL_VALUE_WITH_CONTROL_STYLE}>
                <span css={getValueStyle(row.tone)}>{row.value ?? '—'}</span>
                {row.control}
              </span>
            )
            : <span css={getValueStyle(row.tone)}>{row.value ?? '—'}</span>}
        </div>
      ))}
    </div>
  </section>
);

export const MaterialsSection = ({ materials }: { materials?: readonly PhotoDetailMaterialModel[] }) => (
  <section
    css={PLAIN_SECTION_STYLE}
    data-testid='closeout-photo-detail-materials-section'
    data-closeout-section-variant='plain'
  >
    <h3 css={SECTION_TITLE_STYLE}>MATERIALS</h3>
    {materials?.length
      ? (
        <div css={MATERIALS_TABLE_STYLE}>
          {materials.map(material => (
            <div key={material.id} css={MATERIAL_ROW_STYLE}>
              <span>
                <strong>{material.name}</strong>
                {material.vendorLabel ? <span css={MATERIAL_VENDOR_STYLE}>{material.vendorLabel}</span> : null}
              </span>
              <span>{material.quantityLabel ?? '—'}</span>
              <span>{material.valueLabel ?? '—'}</span>
              <span>{material.statusLabel ?? '—'}</span>
            </div>
          ))}
        </div>
      )
      : <div css={MATERIAL_EMPTY_STYLE}>No materials</div>}
  </section>
);

export const ActivityNotesSection = ({ model }: { model: PhotoDetailModel }) => {
  const activityRows: ActivityNotesItem[] = (model.activity ?? []).map(item => ({
    id: item.id,
    title: item.actor,
    body: item.detail,
    when: item.when,
  }));
  const activityItems: ActivityNotesItem[] = activityRows.length
    ? activityRows
    : getNoteParagraphs(model.notes).map((paragraph, index) => ({
      id: `note-paragraph:${index}`,
      body: paragraph,
    }));
  if (!activityItems.length) return null;

  return (
    <section
      css={PLAIN_SECTION_STYLE}
      data-testid='closeout-photo-detail-activity-section'
      data-closeout-section-variant='timeline'
    >
      <h3 css={SECTION_TITLE_STYLE}>ACTIVITY & NOTES</h3>
      <div css={ACTIVITY_TIMELINE_STYLE}>
        {activityItems.map(item => {
          const tone = getActivityTone(item);
          return (
            <div
              key={item.id}
              css={ACTIVITY_ITEM_STYLE}
              data-testid='closeout-photo-detail-activity-item'
              data-activity-tone={tone}
            >
              <span
                css={ACTIVITY_DOT_STYLE}
                data-testid='closeout-photo-detail-activity-dot'
                data-activity-tone={tone}
                style={getActivityDotStyle(tone)}
                aria-hidden='true'
              />
              <div css={ACTIVITY_ITEM_BODY_STYLE}>
                <div css={ACTIVITY_TEXT_STYLE}>
                  {item.title ? <strong>{item.title}</strong> : null}
                  <span>{item.body}</span>
                </div>
                {item.when ? <div css={ACTIVITY_DATE_STYLE}>{item.when}</div> : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
